import { Dispatch, ReactElement, Ref, SetStateAction, forwardRef, useCallback, useEffect, useState } from "react"
import { useForm } from "react-hook-form";
import axios from "axios";
import { useTranslation } from "react-i18next";

import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Slide,
    Slider,
    Stack,
    Typography,
    Grid,
    Paper,
    Divider
} from "@mui/material"
import { TransitionProps } from "@mui/material/transitions";

// Componentes
import FirstSteps from "./Steps/First";
import TwoSteps from "./Steps/Second";
import ThirdStep from "./Steps/Third";
import FourthStep from "./Steps/Fourth";
import FifthStep from "./Steps/Fifth";
import DetailsUser from "./Steps/DetailsUser";

// Iconos
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useSettings } from "Context/Settings/useSettings";
import { useAuth } from "Context/Auth/useAuth";

const Transition = forwardRef((
    props: TransitionProps & {
        children: ReactElement<any, any>;
    },
    ref: Ref<unknown>,
) => (<Slide in={true} direction="up" ref={ref} {...props} />));

interface PropsInterface {
    open: boolean
    setOpen: Dispatch<SetStateAction<boolean>>
    setReLoad: Dispatch<SetStateAction<boolean>>
    setData: Dispatch<SetStateAction<any>>
    data: any
    urlCredential: string
    urlCreate: string
    urlInformation: string
    roleList: any
    canalList: any
}

const MaxValueProgress = 40
const RegexEmail = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i
const RegexPassword = /^(?=(.*[A-Z]){2,})(?=(.*[a-z]){2,})(?=(.*\d){3,}).*$/

const UserInformationModal = (props: PropsInterface) => {
    const { t } = useTranslation()
    const auth = useAuth()
    const { open, setOpen, setReLoad, data, urlCreate, urlCredential, urlInformation, setData, roleList, canalList } = props
    const { register, handleSubmit, setValue, formState: { errors }, reset, getValues, setError } = useForm()
    // const theme = useTheme();
    const settings = useSettings()
    // const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
    const [loader, setLoader] = useState(false)
    const [createMonitorAccount, setCreateAccountMonitor] = useState(false)

    const [user, setUser] = useState<any>(data)

    const [userHasModified, setUserHasModified] = useState(false)
    const [loaderSendEmail, setLoaderSendEmail] = useState(false)

    const [progressValue, setProgressValue] = useState(0)
    const [roleSelected, setRoleSelected] = useState<any>(null)

    const [messagesError, setMessagesError] = useState<any[]>([])

    const marks = [
        { value: 0, label: 'Paso 1' },
        { value: 10, label: 'Paso 2' },
        { value: 20, label: 'Paso 3' },
        { value: 30, label: 'Paso 4' },
        { value: 40, label: 'Paso 5' },
    ];

    useEffect(() => {
        if (open) {
            if (data !== null) {
                setValue("name", data.name)
                setValue("lastName", data.last_name)
                setValue("document", data.dui)
                setValue("phone", data.phone)
                setValue("email", data.email)
                setValue("router", data.router ?? 0)

                if (data.hasOwnProperty("vendor_code")) {
                    setUser((prevState: any) => ({
                        ...prevState,
                        name: data?.name ?? "",
                        lastName: data?.last_name ?? "",
                        email: data?.email ?? "",
                        phone: data?.phone ?? "",
                        document: data?.dui ?? "",
                        priceList: data?.priceList ?? 0,
                        slp: data?.salesPersonCode ?? 0,
                        codeCellar: data?.codeCellar ?? "",
                        router: data?.router ?? 0,
                        disabled: data.disabled,
                        vendorCode: data?.vendor_code?.split(",") ?? []
                    }))
                }

                if (data.hasOwnProperty("buyer_code")) {
                    setUser((prevState: any) => ({
                        ...prevState,
                        buyerCode: data?.buyer_code?.split(",")
                    }))
                }
            } else {
                setUser({
                    phone: "",
                    document: ""
                })
                setProgressValue(0)
                setRoleSelected(null)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, data])

    const handleClose = () => {
        setOpen(false)
        setData(null)
        setUser(null)
        if (userHasModified) setReLoad(true)
        setUserHasModified(false)
        reset()
    }

    const validateDataUser = async (element: any) => {
        if (data !== null) {
            saveDataUser(element)
            return
        }
        let errorsCopyList: any[] = []
        let attributes: any[] = []
        if (roleSelected === null && progressValue === 0) {
            setMessagesErrorList(t('userControl.text_11'), "role")
            return
        }

        if (progressValue === 0) {
            resetMessageError(["document", "password", "phone"])

            if (user.document === "" || user.document === null || user.document.length < 10) {
                setMessagesErrorList(t('userControl.text_11'), "document")
                return
            }

            if (user.phone === "" || user.phone === null || user.phone.length < 9) {
                setMessagesErrorList(t('userControl.text_11'), "phone")
                return
            }

            if (!RegexPassword.test(element.password)) {
                setMessagesErrorList(t('userControl.text_19'), "password")
                return
            }
            setUser((prevState: any) => ({
                ...prevState,
                ...element,
                buyerCode: [],
                vendorCode: [],
                emailsNotification: [],
                roleMonitor: null
            }))

            setCreateAccountMonitor(roleSelected.id === "barlleno")
            if (roleSelected.id === "barlleno") setProgressValue(prevState => prevState + 10)
        } else if (progressValue === 10) {
            attributes = ["priceList", "slp", "nameSAP", "codeCellar", "router", "roleMonitor"]
            resetMessageError(attributes)
            if (!user.hasOwnProperty("priceList") || user.priceList === "" || user.priceList === null || isNaN(user.priceList)) {
                if (!user.hasOwnProperty("priceList")) errorsCopyList.push({ message: t('userControl.text_11'), id: "priceList" })
                else if (isNaN(user.priceList)) errorsCopyList.push({ message: 'La lista de precios tiene que ser un numero', id: "priceList" })
                else errorsCopyList.push({ message: 'Tiene que ingresar el numero de la lista de precios', id: "priceList" })
            }

            if (!["userStore", "displayer"].includes(roleSelected.id)) {
                if (!user.hasOwnProperty("slp") || user.slp === "" || user.slp === null || isNaN(user.slp)) {
                    if (!user.hasOwnProperty("slp")) errorsCopyList.push({ message: t('userControl.text_11'), id: "slp" })
                    else if (isNaN(user.slp)) errorsCopyList.push({ message: 'El codigo de persona tiene que ser un numero', id: "slp" })
                    else errorsCopyList.push({ message: 'Tiene que ingresar el codigo de persona', id: "slp" })
                }
            }

            if (["admin", "adminsupport", "adminComercial", "userStore"].includes(roleSelected.id)) {
                if (!user.hasOwnProperty("nameSAP") || user.nameSAP === "" || user.nameSAP === null) {
                    if (!user.hasOwnProperty("nameSAP")) errorsCopyList.push({ message: t('userControl.text_11'), id: "nameSAP" })
                    else errorsCopyList.push({ message: 'Tiene que ingresar el nombre de usuario para facturar', id: "nameSAP" })
                }

                if (!user.hasOwnProperty("codeCellar") || user.codeCellar === "" || user.codeCellar === null) {
                    if (!user.hasOwnProperty("codeCellar")) errorsCopyList.push({ message: t('userControl.text_11'), id: "codeCellar" })
                    else errorsCopyList.push({ message: 'Tiene que ingresar el codigo de bodega', id: "codeCellar" })
                } else if (user.codeCellar !== "BOT05" && user.codeCellar !== "BPP01") {
                    if (user.codeCellar !== "BOT05") errorsCopyList.push({ message: 'El codigo de bodega no es valido', id: "codeCellar" })
                    else errorsCopyList.push({ message: 'El codigo de bodega no es valido', id: "codeCellar" })
                }
            }

            if (["admin", "adminsupport", "adminComercial"].includes(roleSelected.id)) {
                if (!user.hasOwnProperty("buyerCode") || user.buyerCode.length === 0) {
                    if (!user.hasOwnProperty("buyerCode")) errorsCopyList.push({ message: t('userControl.text_11'), id: "buyerCode" })
                    else errorsCopyList.push({ message: 'Tiene que ingresar un canal de compra', id: "buyerCode" })
                }
            }

            if (!["displayer"].includes(roleSelected.id)) {
                if (!user.hasOwnProperty("vendorCode") || user.vendorCode.length === 0) {
                    if (!user.hasOwnProperty("vendorCode")) errorsCopyList.push({ message: t('userControl.text_11'), id: "vendorCode" })
                    else errorsCopyList.push({ message: 'Tiene que ingresar un canal de venta', id: "vendorCode" })
                }
            }

            if (["admin", "adminsupport", "adminComercial", "displayerSupervisor", "displayer"].includes(roleSelected.id)) {
                if (!user.hasOwnProperty("router") || user.router === "" || user.router === null || isNaN(user.router)) {
                    if (!user.hasOwnProperty("router")) errorsCopyList.push({ message: t('userControl.text_11'), id: "router" })
                    else if (isNaN(user.router)) errorsCopyList.push({ message: 'El numero de ruta tiene que ser un numero', id: "router" })
                    else errorsCopyList.push({ message: 'Tiene que ingresar el numero de ruta', id: "router" })
                }
            }
            errorsCopyList.forEach(item => setMessagesErrorList(item.message, item.id));

            if (valideIncludeMessages(attributes) || errorsCopyList.length > 0) return
        } else if (progressValue === 20) {
            if (createMonitorAccount) {
                attributes = ["roleMonitor", "vendorCode", "codeCellar", "nameSAP"]
                resetMessageError(attributes)
                if (!user.hasOwnProperty("roleMonitor") || user.roleMonitor === null || user.roleMonitor.id === "") {
                    if (!user.hasOwnProperty("roleMonitor") || user.roleMonitor === null) errorsCopyList.push({ message: t('userControl.text_11'), id: "roleMonitor" })
                    else errorsCopyList.push({ message: 'Tiene que seleccionar un rol', id: "roleMonitor" })
                }

                if (!user.hasOwnProperty("codeCellar") || user.codeCellar === "" || user.codeCellar === null) {
                    if (!user.hasOwnProperty("codeCellar")) errorsCopyList.push({ message: t('userControl.text_11'), id: "codeCellar" })
                    else errorsCopyList.push({ message: 'Tiene que ingresar el codigo de bodega', id: "codeCellar" })
                } else if (user.codeCellar !== "BOT05" && user.codeCellar !== "BPP01") {
                    if (user.codeCellar !== "BOT05") errorsCopyList.push({ message: 'El codigo de bodega no es valido', id: "codeCellar" })
                    else errorsCopyList.push({ message: 'El codigo de bodega no es valido', id: "codeCellar" })
                }

                if (user.roleMonitor?.id === "cajero" || roleSelected.id === "userStore") {
                    if (!user.hasOwnProperty("nameSAP") || user.nameSAP === "" || user.nameSAP === null) {
                        if (!user.hasOwnProperty("nameSAP")) errorsCopyList.push({ message: t('userControl.text_11'), id: "nameSAP" })
                        else errorsCopyList.push({ message: 'Tiene que ingresar el nombre de usuario para facturar', id: "nameSAP" })
                    }
                }
                errorsCopyList.forEach(item => setMessagesErrorList(item.message, item.id));
                if (valideIncludeMessages(attributes) || errorsCopyList.length > 0) return
            }
        }

        if (progressValue !== MaxValueProgress) {
            setProgressValue(prevState => prevState + 10)
            return
        }
        saveDataUser(element)
    }

    const setMessagesErrorList = (message: string, id: string) => {
        setMessagesError(prevState => {
            let values = prevState.filter((element: any) => element.id !== id)
            return [
                ...values,
                {
                    id: id,
                    message: message
                }
            ]
        })
    }

    const valideIncludeMessages = useCallback((id: string[]): boolean => {
        return messagesError.some((element: any) => id.includes(element.id))
        // eslint-disable-next-line
    }, [])

    const resetMessageError = useCallback((id: string[]) => {
        setMessagesError(prevState => {
            return prevState.filter((element: any) => !id.includes(element.id))
        })
    }, [])

    const saveDataUser = async (element: any) => {
        setLoader(true)
        let headers = {
            "Content-Type": "application/json",
            "Authorization": "UserToken " + (auth.auth?.token ?? "")
        }
        if (data === null) {
            let body = {
                email: element.email,
                password: element.password,
                role: roleSelected.id,
                data: {
                    uid: "MmszRip8dZVcUaN5KZVRX7C4U463",
                    device: "web",
                    dui: user.document ?? "",
                    language: "es",
                    last_name: user.lastName ?? "",
                    name: user.name ?? "",
                    phone: user.phone ?? "",
                    priceList: user.priceList ?? null,
                    vendor_code: user.vendorCode?.join(",") ?? "-",
                    salesPersonCode: Number(user.slp) ?? -1,
                    router: user.router ?? 0,
                    emails_notification: user.emailsNotification ?? [],
                    codeCellar: user.codeCellar ?? "",
                    vendorName: user.nameSAP ?? "",
                    buyer_code: user.buyerCode?.join(",") ?? "-",
                }
            }

            await axios.post(urlCreate, body, { headers }).then((response) => {
                setUserHasModified(true)
                settings.setSnackBar({
                    message: response?.data?.message ?? t('userControl.text_36'),
                    type: 'success',
                    open: true
                })
                setLoader(false)
                handleClose()
            }).catch((error) => {
                settings.setSnackBar({
                    message: error?.response?.data?.message ?? t('userControl.text_37'),
                    type: "error",
                    open: true
                })
                setLoader(false)
            })
            setLoader(false)
        } else {
            if (element.password !== "" || element.email !== data.email || data.disabled !== user?.disabled) {
                let body: any = {
                    userKey: data.$key,
                }
                if (element.password !== "") {
                    if (element.password.length < 8) {
                        setError("password", { type: 'custom', message: t('userContro.text_19') })
                        setLoader(false)
                        return
                    }
                    body.password = element.password
                }
                if (element.email !== data.email) body.email = element.email
                if (element.disabled !== user?.disabled) body.disabled = user?.disabled

                if (Object.keys(body).length > 1) {
                    await axios.put(urlCredential, body, { headers }).then((response) => {
                        setUserHasModified(true)
                        settings.setSnackBar({
                            message: response?.data?.message ?? t('userControl.text_35'),
                            type: "success",
                            open: true
                        })
                        setLoader(false)
                    }).catch((error) => {
                        settings.setSnackBar({
                            message: error?.response?.data?.message ?? t('userControl.text_38'),
                            type: "error",
                            open: true
                        })
                        setLoader(false)
                    })
                }
                setLoader(false)
            }

            let userBody: any = {
                userKey: data.$key
            }

            if (element.name !== data.name) userBody.name = element.name
            if (element.lastName !== data.last_name) userBody.lastName = element.lastName
            if (element.phone !== user.phone) userBody.phone = user.phone
            if (element.document !== user.dui) userBody.dui = user.document

            if (Object.keys(userBody).length > 1) {
                await axios.put(urlInformation, userBody, { headers }).then((response) => {
                    settings.setSnackBar({
                        message: response?.data?.message ?? t('userControl.text_39'),
                        open: true,
                        type: "success"
                    })
                    setUserHasModified(true)
                    setLoader(false)
                }).catch((error) => {
                    settings.setSnackBar({
                        message: error?.response?.data?.message ?? t('userControl.text_40'),
                        open: true,
                        type: "error"
                    })
                    setLoader(false)
                })
            } else setLoader(false)
        }
    }

    const handleDelete = (item: any, type: string) => {
        if (type === "buyerCode") {
            let newBuyerCode = user?.buyerCode?.filter((element: any) => element !== item)
            setUser((prev: any) => ({ ...prev, [type]: newBuyerCode }))
        } else if (type === "vendorCode") {
            let newVendorCode = user?.vendorCode?.filter((element: any) => element !== item)
            setUser((prev: any) => ({ ...prev, [type]: newVendorCode }))
        } else {
            let newEmailsNotification = user?.emailsNotification?.filter((element: any) => element !== item)
            setUser((prev: any) => ({ ...prev, [type]: newEmailsNotification }))
        }
    }

    const addCanal = (type: string, valueInput: string | null, setValueInput: Dispatch<SetStateAction<string | null>>) => {
        if (valueInput === null) return

        if (type === "buyerCode") {
            if (valueInput?.length < 4) setMessagesErrorList("Canal no valido, tiene que tener un minimo de 4 caracteres", "buyerCode")
            else {
                resetMessageError(["buyerCode"])
                let validate = user?.buyerCode?.find((item: any) => item === valueInput)
                if (!validate) setUser((prev: any) => ({ ...prev, [type]: [...prev[type], valueInput] }))
            }
        } else if (type === "vendorCode") {
            resetMessageError(["vendorCode"])
            let validate = user?.vendorCode?.find((item: any) => item === valueInput)
            if (!validate) {
                setUser((prev: any) => ({ ...prev, [type]: [...prev[type], valueInput] }))
            }
        } else {
            if (RegexEmail.test(valueInput)) {
                resetMessageError(["emailsNotification"])
                let validate = user?.emailsNotification?.find((item: any) => item === valueInput)
                if (!validate) setUser((prev: any) => ({ ...prev, [type]: [...prev[type], valueInput] }))
            } else setMessagesErrorList("Correo no valido", "emailsNotification")
        }
        setValueInput(null)
    }

    const valuetext = (value: number): string => `${value}`;

    const handleBack = () => {
        if (progressValue === 20 && roleSelected?.id === "barlleno") setProgressValue(prevState => prevState - 20)
        else setProgressValue(prevState => prevState - 10)
    }

    // Helper para avatar initials
    const initials = (name?: string, last?: string) => {
        const n = (name ?? "").trim().split(" ")[0] ?? ""
        const l = (last ?? "").trim().split(" ")[0] ?? ""
        return (n.charAt(0) + (l.charAt(0) ?? "")).toUpperCase()
    }

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            maxWidth="md"
            fullWidth
            keepMounted
            fullScreen={settings.isMobile}
            onClose={(loader || loaderSendEmail) ? () => { } : handleClose}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: 6,
                }
            }}
        >
            {/* Header: compact, avatar + title + close */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2,
                    bgcolor: '#ebebebff',
                    color: '#fff'
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48 }}>
                        {initials(user?.name ?? data?.name ?? "", user?.lastName ?? data?.last_name ?? "")}
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {data ? (data.name ?? "") + " " + (data.last_name ?? "") : (user?.name ? `${user?.name} ${user?.lastName ?? ""}` : t('userControl.text_5'))}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {data ? data.email : (user?.phone ?? "")}
                        </Typography>
                    </Box>
                </Stack>

                {!loader && !loaderSendEmail && (
                    <IconButton onClick={handleClose} aria-label="Cerrar" sx={{ color: '#173456' }}>
                        <CloseIcon />
                    </IconButton>
                )}
            </Box>

            {/* Step slider (subtle) */}
            {data === null && (
                <Box sx={{ px: 3, py: 2, backgroundColor: 'background.paper' }}>
                    <Slider
                        defaultValue={0}
                        getAriaValueText={valuetext}
                        step={10}
                        disabled
                        value={progressValue}
                        max={MaxValueProgress}
                        marks={marks}
                        sx={{
                            color: 'rgba(0,0,0,0.08)',
                            // color: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.08)' : undefined,
                            "& .MuiSlider-thumb": {
                                // color: theme.palette.primary.main,
                                width: 12,
                                height: 12,
                            },
                            '&.Mui-disabled .MuiSlider-track': {
                                // color: theme.palette.primary.main
                            }
                        }}
                        valueLabelDisplay="off"
                    />
                </Box>
            )}

            <form onSubmit={handleSubmit(validateDataUser)}>
                <DialogContent
                    sx={{
                        px: 3,
                        pt: 2,
                        pb: 1,
                        bgcolor: 'background.default',
                        maxHeight: { xs: '68vh', md: '72vh' },
                        overflowY: 'auto'
                    }}
                >
                    {/* Clean, minimal panels */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={7}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                    Información Básica
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Box>
                                    {(data !== null || progressValue === 0) && (
                                        <FirstSteps
                                            errors={errors}
                                            t={t}
                                            register={register}
                                            getValues={getValues}
                                            data={data}
                                            regexEmail={RegexEmail}
                                            regexPassword={RegexPassword}
                                            loaderSendEmail={loaderSendEmail}
                                            setLoaderSendEmail={setLoaderSendEmail}
                                            roleList={roleList?.rolesList ?? []}
                                            messagesError={messagesError}
                                            roleSelected={roleSelected}
                                            setMessages={setMessagesError}
                                            setRoleSelected={setRoleSelected}
                                            user={user}
                                            setUser={setUser}
                                        />
                                    )}
                                </Box>
                            </Paper>

                            {/* Additional steps stacked under the main form for small screens */}
                            <Box sx={{ mt: 2 }}>
                                {(data === null && progressValue === 10) && (
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{t('userControl.step2_title') ?? 'Configuración'}</Typography>
                                        <Divider sx={{ mb: 1 }} />
                                        <TwoSteps
                                            role={roleSelected}
                                            user={user}
                                            canalList={canalList}
                                            errorsList={messagesError}
                                            deleteList={handleDelete}
                                            addList={addCanal}
                                            setUser={setUser}
                                        />
                                    </Paper>
                                )}

                                {(data === null && progressValue === 20) && (
                                    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{t('userControl.step3_title') ?? 'Monitor'}</Typography>
                                        <Divider sx={{ mb: 1 }} />
                                        <ThirdStep
                                            user={user}
                                            role={roleSelected}
                                            errorsList={messagesError}
                                            createMonitorAccount={createMonitorAccount}
                                            setCreateAccountMonitor={setCreateAccountMonitor}
                                            deleteList={handleDelete}
                                            addList={addCanal}
                                            setUser={setUser}
                                        />
                                    </Paper>
                                )}

                                {(data === null && progressValue === 30) && <FourthStep />}

                                {(data === null && progressValue === 40) && (
                                    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                                        <FifthStep role={roleSelected} user={user} />
                                    </Paper>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={5}>
                            {/* Summary / Details column */}
                            <Card variant="outlined" sx={{ mb: 2 }}>
                                <CardContent>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                        Resumen
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />

                                    <Stack spacing={1}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Nombre</Typography>
                                            <Typography variant="body2">{(user?.name ?? data?.name ?? '') + ' ' + (user?.lastName ?? data?.last_name ?? '')}</Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Documento</Typography>
                                            <Typography variant="body2">{user?.document ?? data?.dui ?? '-'}</Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Teléfono</Typography>
                                            <Typography variant="body2">{user?.phone ?? data?.phone ?? '-'}</Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Email</Typography>
                                            <Typography variant="body2">{(user?.email ?? data?.email) ?? '-'}</Typography>
                                        </Box>

                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Rol</Typography>
                                            <Typography variant="body2">{roleSelected?.name ?? (data?.roleName ?? '-')}</Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>

                            {/* When viewing existing user, show details compact */}
                            {data !== null ? (
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Detalles</Typography>
                                    <Divider sx={{ mb: 1 }} />
                                    <DetailsUser
                                        data={data}
                                        t={t}
                                        roleList={roleList?.rolesList ?? []}
                                        user={user}
                                        setUser={setUser}
                                    />
                                </Paper>
                            ) : (
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{t('userControl.tips') ?? 'Consejos'}</Typography>
                                    <Divider sx={{ mb: 1 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {t('userControl.help_create') ?? 'Completa los pasos para crear la cuenta. Usa el panel de la izquierda para editar campos.'}
                                    </Typography>
                                </Paper>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ minWidth: 120 }}>
                            {progressValue !== 0 && (
                                <Button
                                    variant="text"
                                    startIcon={<ArrowBackIosIcon />}
                                    disabled={loader}
                                    onClick={handleBack}
                                >
                                    {t('userControl.text_33')}
                                </Button>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                                variant="submit-button"
                                // color="secondary"
                                disabled={loader}
                                type="submit"
                                startIcon={loader ? <CircularProgress color="inherit" size={18} /> : undefined}
                                sx={{ minWidth: 160 }}
                            >
                                {loader ? t('userControl.text_31') : (data !== null ? t('benchmarks.text_13') : (progressValue !== MaxValueProgress ? t('userControl.text_32') : t('benchmarks.text_13')))}
                            </Button>
                        </Box>
                    </Stack>
                </DialogActions>
            </form>
        </Dialog>
    )
}

export default UserInformationModal